import React, { useState } from 'react';
import {
  Briefcase,
  User,
  Calendar,
  Compass,
  Award,
  FileText,
  Clock,
  Zap,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Plus,
  Send,
  Printer,
  Sparkles,
  Edit3,
  Shield,
  Tag,
  BookOpen
} from 'lucide-react';
import {
  PracticeCase,
  CaseStage,
  PriorityLevel,
  ReportStatus
} from '../../types/practiceManagement';
import { Language, CompleteKundali } from '../../types/kundali';

interface CaseWorkspaceProps {
  caseItem: PracticeCase;
  lang: Language;
  onBack: () => void;
  onUpdateCase: (updated: PracticeCase) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
  onOpenAcharyaConsultation?: (caseItem: PracticeCase) => void;
  onPrepareReport?: (caseItem: PracticeCase) => void;
  onViewClient?: (clientId: string) => void;
}

export const CaseWorkspace: React.FC<CaseWorkspaceProps> = ({
  caseItem,
  lang,
  onBack,
  onUpdateCase,
  onOpenProAnalysis,
  onOpenAcharyaConsultation,
  onPrepareReport,
  onViewClient
}) => {
  const isHi = lang === 'hi';
  const [activeTab, setActiveTab] = useState<
    'findings' | 'notes' | 'remedies' | 'timing' | 'followup'
  >('findings');

  const [privateNotes, setPrivateNotes] = useState(caseItem.practitionerPrivateNotes || '');
  const [clientSummary, setClientSummary] = useState(caseItem.clientFacingSummaryNotes || '');
  const [isSaved, setIsSaved] = useState(false);

  const handleSaveNotes = () => {
    const updated: PracticeCase = {
      ...caseItem,
      practitionerPrivateNotes: privateNotes,
      clientFacingSummaryNotes: clientSummary,
      lastActivityDate: new Date().toISOString().split('T')[0]
    };
    onUpdateCase(updated);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2500);
  };

  const handleStageChange = (newStage: CaseStage) => {
    const updated: PracticeCase = {
      ...caseItem,
      stage: newStage,
      lastActivityDate: new Date().toISOString().split('T')[0]
    };
    onUpdateCase(updated);
  };

  return (
    <div className="space-y-4">
      {/* Top Header & Breadcrumb */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card dark:hover:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted transition-colors cursor-pointer"
              title={isHi ? 'पीछे जाएं' : 'Back to Pipeline'}
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono font-bold text-[#52635F] dark:text-amber-400 bg-astro-secondary-surface dark:bg-amber-950/40 px-2.5 py-0.5 rounded border border-[#E2DED5] dark:border-amber-800">
                  {caseItem.caseNumber}
                </span>
                <span className="text-sm font-black text-astro-text dark:text-astro-muted">
                  {caseItem.clientName}
                </span>
                {caseItem.clientRefId && (
                  <span className="text-xs text-astro-muted font-mono">({caseItem.clientRefId})</span>
                )}
              </div>
              <div className="text-xs font-bold text-astro-secondary dark:text-amber-300 pt-0.5">
                {isHi ? caseItem.themeTitleHi : caseItem.themeTitleEn}
              </div>
            </div>
          </div>

          {/* Primary Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {onOpenProAnalysis && (
              <button
                onClick={() => onOpenProAnalysis(caseItem)}
                className="min-h-[38px] px-3 py-1.5 rounded-lg bg-astro-secondary hover:bg-amber-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
              >
                <Compass className="w-4 h-4" />
                <span>{isHi ? 'विशारद विश्लेषण (Pro)' : 'Pro Analysis'}</span>
              </button>
            )}

            {onOpenAcharyaConsultation && (
              <button
                onClick={() => onOpenAcharyaConsultation(caseItem)}
                className="min-h-[38px] px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
              >
                <Award className="w-4 h-4" />
                <span>{isHi ? 'आचार्य परामर्श (Acharya)' : 'Consult'}</span>
              </button>
            )}

            {onPrepareReport && (
              <button
                onClick={() => onPrepareReport(caseItem)}
                className="min-h-[38px] px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
              >
                <FileText className="w-4 h-4" />
                <span>{isHi ? 'रिपोर्ट तैयार करें' : 'Prepare Report'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Stage & Meta Strip */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-astro-border dark:border-astro-strong-border text-xs">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-astro-muted dark:text-astro-muted">
                {isHi ? 'वर्तमान चरण:' : 'Stage:'}
              </span>
              <select
                value={caseItem.stage}
                onChange={(e) => handleStageChange(e.target.value as CaseStage)}
                className="font-bold text-xs px-2.5 py-1 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              >
                <option value="NEW">New</option>
                <option value="ANALYSIS">Analysis</option>
                <option value="REVIEW">Review</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="REPORT_PREP">Report Prep</option>
                <option value="DELIVERED">Delivered</option>
                <option value="FOLLOW_UP">Follow-Up</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5 text-astro-secondary-text dark:text-astro-muted">
              <User className="w-3.5 h-3.5" />
              <span>{caseItem.assignedPractitioner}</span>
            </div>

            <div className="flex items-center gap-1.5 text-astro-muted dark:text-astro-muted">
              <Clock className="w-3.5 h-3.5" />
              <span>Created: {caseItem.createdDate}</span>
            </div>
          </div>

          {caseItem.timingAlertIndicator?.hasAlert && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-astro-secondary-surface0/15 border border-astro-accent/30 text-[#52635F] dark:text-amber-300 font-bold text-xs">
              <Zap className="w-3.5 h-3.5" />
              <span>{caseItem.timingAlertIndicator.title}</span>
            </div>
          )}
        </div>
      </div>

      {/* Target Question Highlight Box */}
      <div className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent p-4 rounded-xl border border-astro-accent/30">
        <div className="text-[11px] font-bold uppercase tracking-wider text-astro-secondary dark:text-amber-400 mb-1">
          {isHi ? 'मुख्य परामर्श प्रश्न (Target Question)' : 'Primary Client Target Question'}
        </div>
        <div className="text-sm font-semibold text-astro-text dark:text-astro-muted">
          "{caseItem.targetQuestion}"
        </div>
      </div>

      {/* Workspace Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-astro-border dark:border-astro-strong-border pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('findings')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'findings'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'प्रमाण व निष्कर्ष (Findings)' : 'Deterministic Findings'}
        </button>

        <button
          onClick={() => setActiveTab('timing')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'timing'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'फल-प्रदाता काल (Timing Windows)' : 'Timing Windows'}
        </button>

        <button
          onClick={() => setActiveTab('notes')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'notes'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'परामर्श नोट्स (Case Notes)' : 'Case & Session Notes'}
        </button>

        <button
          onClick={() => setActiveTab('remedies')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'remedies'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'उपाय व संस्तुति (Remedies)' : 'Prescribed Remedies'}
        </button>

        <button
          onClick={() => setActiveTab('followup')}
          className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'followup'
              ? 'bg-astro-secondary-surface0 text-white shadow-xs'
              : 'text-astro-secondary-text dark:text-astro-muted hover:bg-astro-secondary-surface dark:hover:bg-astro-secondary'
          }`}
        >
          {isHi ? 'फॉलो-अप योजना (Follow-Up)' : 'Follow-Up Plan'}
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'findings' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span>{isHi ? 'जन्मजात क्षमता व प्रतिज्ञा' : 'Natal Promise Verdict'}</span>
            </h3>
            <div className="text-sm font-bold text-astro-text dark:text-astro-muted bg-astro-secondary-surface dark:bg-astro-primary p-3 rounded-lg border border-astro-border dark:border-astro-strong-border">
              {caseItem.deterministicFindings?.natalPromiseVerdict ||
                'High promise confirmed by Lagnesha and 10th lord placement.'}
            </div>

            <div className="space-y-1.5 pt-2">
              <span className="text-xs font-bold text-astro-secondary-text dark:text-astro-muted">
                {isHi ? 'मुख्य शास्त्रीय प्रमाण (Supporting Factors):' : 'Key Supporting Classical Factors:'}
              </span>
              <ul className="space-y-1.5 text-xs text-astro-secondary-text dark:text-astro-muted">
                {(caseItem.deterministicFindings?.keySupportingFactors || [
                  '10th Lord in Trikona with benefic aspect',
                  'Favorable Sarvashtakavarga bindu allocation',
                  'Operating Dasha lord strongly placed in Varga'
                ]).map((factor, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{factor}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500" />
              <span>{isHi ? 'सक्रिय दशा एवं गोचर' : 'Active Dasha & Transit Confluence'}</span>
            </h3>

            <div className="space-y-2 text-xs">
              <div className="bg-astro-secondary-surface dark:bg-astro-primary p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border flex justify-between">
                <span className="text-astro-muted">Primary Dasha:</span>
                <span className="font-bold text-astro-text dark:text-astro-muted">
                  {caseItem.deterministicFindings?.primaryDasha || 'Operating Vimshottari Phase'}
                </span>
              </div>

              <div className="bg-astro-secondary-surface dark:bg-astro-primary p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border flex justify-between">
                <span className="text-astro-muted">Transit Trigger:</span>
                <span className="font-bold text-astro-text dark:text-astro-muted">
                  {caseItem.deterministicFindings?.primaryTransitTrigger || 'Jupiter/Saturn confluence'}
                </span>
              </div>

              <div className="bg-astro-secondary-surface dark:bg-astro-primary p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border flex justify-between">
                <span className="text-astro-muted">Projected Native Age:</span>
                <span className="font-bold text-[#52635F] dark:text-amber-400">
                  {caseItem.deterministicFindings?.projectedFructificationAge || 'Age 34'}
                </span>
              </div>

              <div className="bg-astro-secondary-surface dark:bg-astro-primary p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border flex justify-between">
                <span className="text-astro-muted">Varga Affirmation:</span>
                <span className="font-bold text-astro-text dark:text-astro-muted">
                  {caseItem.deterministicFindings?.vargaAffirmation || 'Relevant divisional chart confirms'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'timing' && (
        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <span>{isHi ? 'सटीक फल-प्रदाता समय काल' : 'Phala-Bhuta Timing Windows'}</span>
          </h3>

          <div className="p-3.5 bg-astro-secondary-surface0/10 rounded-lg border border-astro-accent/30 text-xs text-[#263633] dark:text-amber-200">
            {caseItem.timingWindowsSummary ||
              'Primary Window: High Confluence alignment in upcoming Dasha-Gochar trigger. Secondary consolidation window following 6 months.'}
          </div>

          <div className="text-xs text-astro-secondary-text dark:text-astro-muted">
            Note: Fructification timing is derived deterministically from the Vimshottari dasha progression, Ashtakvarga transits, and divisional chart confirmations.
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Private Practitioner Notes */}
            <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text dark:text-astro-muted flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-amber-500" />
                  <span>{isHi ? 'गोपनीय आचार्य नोट्स (Private Notes)' : 'Practitioner Private Case Notes'}</span>
                </span>
                <span className="text-[10px] bg-astro-secondary-surface dark:bg-astro-secondary px-2 py-0.5 rounded text-astro-muted">
                  Not in client report
                </span>
              </div>
              <textarea
                value={privateNotes}
                onChange={(e) => setPrivateNotes(e.target.value)}
                rows={7}
                placeholder="Record clinical observations, sensitive planetary tensions, or background counsel..."
                className="w-full p-3 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>

            {/* Client-Facing Summary */}
            <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text dark:text-astro-muted flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-indigo-500" />
                  <span>{isHi ? 'क्लाइंट सारांश (Client Summary)' : 'Client-Facing Summary Notes'}</span>
                </span>
                <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded border border-indigo-200 dark:border-indigo-800">
                  Included in Dossier
                </span>
              </div>
              <textarea
                value={clientSummary}
                onChange={(e) => setClientSummary(e.target.value)}
                rows={7}
                placeholder="Write refined client-facing guidance, auspicious timing windows, and executive takeaways..."
                className="w-full p-3 text-xs bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            {isSaved && (
              <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                <span>Notes Saved Successfully</span>
              </span>
            )}
            <button
              onClick={handleSaveNotes}
              className="px-4 py-2 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              {isHi ? 'नोट्स सुरक्षित करें' : 'Save Notes'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'remedies' && (
        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted">
              {isHi ? 'संस्तुत शास्त्रीय उपाय' : 'Prescribed Classical Remedies'}
            </h3>
          </div>

          <div className="space-y-2">
            {(caseItem.prescribedRemedies || []).map((rem) => (
              <div
                key={rem.id}
                className="p-3 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border flex items-start justify-between gap-3 text-xs"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-astro-text dark:text-astro-muted">{rem.title}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-astro-secondary/15 dark:bg-astro-secondary/40 text-astro-secondary dark:text-amber-300">
                      {rem.category}
                    </span>
                  </div>
                  <div className="text-astro-secondary-text dark:text-astro-muted text-[11px] pt-1">
                    {rem.description}
                  </div>
                </div>
                <span className="text-[11px] text-astro-muted font-semibold shrink-0">
                  {rem.duration}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'followup' && (
        <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-astro-muted dark:text-astro-muted">
            {isHi ? 'फॉलो-अप योजना एवं आगामी मील के पत्थर' : 'Follow-Up Plan & Timing Check-In'}
          </h3>

          <div className="p-3 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-astro-muted">Recommended Follow-Up Date:</span>
              <span className="font-bold text-astro-text dark:text-astro-muted">
                {caseItem.followUpPlan?.recommendedDate || 'Within 30 Days'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-astro-muted">Milestone Reason:</span>
              <span className="font-semibold text-astro-text dark:text-astro-muted">
                {caseItem.followUpPlan?.reason || 'Contract review / Timing trigger check-in'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
